import { z } from 'zod';

/**
 * Layer Architecture (Phase L.1) — operator layer preferences contract.
 *
 * `LAYER_KEYS` is the single source of truth for the set of persisted,
 * operator-toggleable map layers; `LAYER_PREFERENCE_DEFAULTS` (every key's L.0
 * default-on state) is DERIVED from the default-view layer lock below — it is
 * shared by:
 *   - the DB (`operator_layer_preferences.layer_key`),
 *   - the API (GET/PUT /api/operator-preferences/layers), and
 *   - the web layer model (`layerGroups.ts` maps each key to a LayerPrefs field).
 *
 * Keys are stable kebab-case identifiers grouped by doctrinal layer group
 * (L.2: Default / Fire Risk / Hazard / Flood Risk / Ice Storm Risk / Asset /
 * Context). The four Default-group data layers default ON (the L.0 baseline COP);
 * everything else defaults OFF, except the documented non-view chrome (the EWW
 * mute, the road-closures-restrictions sub-filter, incident-scoped sectors, and
 * the provincial-boundary base-map furniture).
 *
 * Placeholder layers whose data ships in a later phase (Air Quality, Earthquakes,
 * Public Alerts, Hydro Repair Crews, EMS) are deliberately NOT listed here — they
 * are disabled "Coming in Phase X" rows that never toggle, so nothing to persist.
 */
export const LAYER_KEYS = [
  // 🎯 Default (L.0) — the always-on baseline COP at country zoom.
  'live-video-feeds',
  'cameras-smoke-priority', // sub-filter of Live Video Feeds
  'urgent-anomalies', // "Urgent Alerts" (Default group)
  'aircraft', // "Aircraft & Marine" (Default group)
  'reported-oc-wildfires', // OC-FUL red flame (Default Layer 4)
  // 🔥 Fire Risk (multi-select, L.2) — FWI heatmap (E.2.2, CWFIS WMS) + the
  // all-stages wildfire layer. They can stack (no radio).
  'fire-weather-index',
  'all-reported-wildfires', // "Reported Wildfires (all stages)"
  // E.W.0.5 — Raw Hot Spots (pre-fusion satellite thermal). A FIRE-RISK layer,
  // NOT part of the locked default view (#202/#236) — defaults OFF like every
  // other non-Default-group layer; RawHotspotsLayer additionally gates rendering
  // to regional+ zoom.
  'raw-hotspots',
  // #370 de-flood — Fusion Candidates: the POST-fusion below-urgent mass (IMS
  // Response Level 1/2 high_risk_hotspots) the density-arm gate demoted off the
  // Urgent Alerts surface. A FIRE-RISK layer, default OFF (auto-OFF needs no
  // founder lock auth); FusionCandidatesLayer gates rendering to regional+ zoom.
  'fusion-candidates',
  // E.W.5 — Camera Smoke Detections (pre-fusion camera-CV smoke). The camera-CV
  // sibling of Raw Hot Spots; ambient amber camera-glyph dots; self-gates to
  // regional+ zoom like raw-hotspots.
  'camera-smoke-detections',
  // ⚠️ Hazard (L.2): Extreme Weather Warnings (EWW) — a SUBTRACTIVE mute on the
  // Urgent-Alerts CAP-polygon subset. Defaults ON (non-view chrome: muting it
  // would HIDE part of an authorized default layer).
  'extreme-weather-warnings',
  // ⚠️ Hazard → "Evacuation Orders & Alerts" (#204) — GeoBC / Emergency Management
  // BC order & alert AREA polygons from the arcgis_public_features ingest (mig
  // 0063). EvacuationOrdersLayer self-gates to regional+ zoom.
  'bc-evacuation-orders',
  // ⚠️ Hazard → "Evacuation Orders — recorded in-COP" (#445-(3)) — OPERATOR-DECLARED
  // evac orders (operator_evacuation_orders, mig 0115), drawn + recorded on behalf
  // of a named issuing authority. A PARALLEL layer to bc-evacuation-orders (never
  // conflated). Defaults OFF (absent from the locked default arrays).
  'operator-evacuation-orders',
  // 🔥 Fire Risk → "Fire Bans & Restrictions" (#213). The Alberta Fire Ban System
  // + Fire Control Orders, from the arcgis_public_features `fire_restriction`
  // category (mig 0063 wave 2).
  'ab-fire-bans',
  // 📋 Advisories & Declarations (#213) — the remaining ingested public-ArcGIS
  // category feeds, surfaced via the generalized config-driven renderer. Each
  // self-gates to regional+ zoom.
  'drought-conditions', // BC drought-level basins + MB river/lake flow stations
  'boil-water-advisories', // MB public water systems
  'bc-state-of-local-emergency', // SoLE & Band Council Resolutions
  'tropical-cyclones', // US-NHC forecast positions + error cone (ECCC CHC webmap)
  // ECCC GeoMet hurricane forecast TRACK LINE (#212) — official WMS raster
  // overlay (HURRICANE_LINE), client-side, no ingest. Ambient register
  // (opacity ≤0.55). Off Atlantic-season most of the year (honest empty).
  'eccc-hurricane-track',
  // 🚒 Asset (multi-select).
  'sar-aviation',
  'fire-stations',
  'wildfire-bases',
  'ontario-corps',
  // L.2.2: Drone Operator Coverage (§3B.6) — base marker + coverage area + ETA.
  'drone-operator-coverage',
  // R.4.2: Staging Areas — ambient depot glyphs for open IMS staging areas (§2.5/§6.4).
  'staging-areas',
  // Task #64: Sectors — operator-drawn zone-of-concern polygons on an incident
  // (IMS §5.3; §4.3 ambient dashed boundary, never pulse). Defaults ON (non-view
  // chrome: they only exist on sectored incidents, so nothing clutters a fresh view).
  'sectors',
  // R5 Slice 3 (#507): Area Command — the §4.6 oversight-entity marker (parent
  // incidents with is_area_command=true + their live child-incident count).
  // Ambient register, STATIC, defaults OFF (absent from the locked default
  // arrays — no founder lock edit needed); the layer-picker row carries the live
  // count ("Area Command (N)") even while off, the PEOC first-notice cue.
  'area-command',
  // 🌊 Flood Risk + 🧊 Ice Storm Risk (W.2) — Vulnerable Communities render.
  // Hazard-agnostic community layer mounted under two group-affinity toggles
  // (both back the same /api/communities data).
  'flood-vulnerable-communities',
  'ice-storm-vulnerable-communities',
  // Task #189 — Vulnerability shading sub-toggle on the community layer: when a
  // community layer is ON, this ADDS the derived social-vulnerability band to the
  // marker chip. Contextual encoding, never part of the default view (PART IX).
  'community-vulnerability-shading',
  // Ice Storm Risk → "Power Outages" (utility_outages render, E.1.9/E.1.10).
  // Self-gates to regional+ zoom.
  'ice-storm-power-outages',
  // Ice Storm Risk → "Road Closures" (R.1f road_closures render; 511 ON/AB +
  // DriveBC). Self-gates to regional+ zoom.
  'ice-storm-road-closures',
  // Road Closures fine-control sub-filter (the L.1 Extreme-Weather-mute pattern):
  // when the Road Closures layer is ON, false MUTES the lane/partial restriction
  // dots. Defaults ON = restrictions shown (non-view chrome: inert unless the
  // parent layer is on, which it is not by default).
  'ice-storm-road-closures-restrictions',
  // Ice Storm Risk → "Estimated Comms Degradation" (#109 comms_degradation_zones).
  // Fusion-derived ESTIMATE zone; self-gates to regional+ zoom.
  'ice-storm-comms-degradation',
  // Flood Risk → "Hydrometric Gauges" (E.F.1 hydrometric_observations render).
  // Self-gates to regional+ zoom.
  'hydrometric-gauges',
  // Flood Risk → "3D Flood Inundation (modelled)" (#177, Cesium-wow). A MODELLED
  // what-if; renders only in the bounded demo regions.
  'flood-inundation',
  // NRCan EGS "Active Floods in Canada" observed flood-EXTENT raster (#212) —
  // official WMS overlay, client-side, no ingest. Ambient register (opacity ≤0.55).
  'nrcan-flood-extent',
  // 🛰️ Context (multi-select).
  'weather-stations',
  'provincial-boundaries', // base-map furniture; defaults ON (non-view chrome)
  // #262 (founder ruling 2026-06-11) — Viewshed-on-select. The #178 terrain
  // line-of-sight viewshed is part of what a camera IS: selecting ANY camera
  // auto-drapes its viewshed (async; released on deselect). This key is the
  // per-operator "hide viewshed" opt-out — NOT a map layer (the old standing
  // 'camera-viewshed' Fire-Risk toggle is REMOVED; PART IX: contextual render
  // beats a standing toggle). Like 'briefing-mode' below, layerGroups.ts
  // deliberately does NOT home it in a doctrinal group. Defaults ON (honest
  // default — the ruling makes the drape the norm); stale stored
  // 'camera-viewshed' rows are ignored by resolveLayerPreferences (unknown
  // keys drop out).
  'viewshed-on-select',
  // B.1 — Briefing-Up view-mode toggle (operator-cop-doctrine §3B.7/§6.3). NOT a
  // map layer: persisted here so the operator's [Operator | Briefing] mode choice
  // survives a reload + syncs cross-device. layerGroups.ts deliberately does NOT
  // home it in a doctrinal group, so it never renders as a layer toggle.
  'briefing-mode',
] as const;

export type LayerKey = (typeof LAYER_KEYS)[number];

/**
 * DEFAULT-VIEW LAYER LOCK (#202) — the single source of truth for the L.0
 * baseline COP, shared by BOTH sides of the contract:
 *   - web: `apps/web/src/prefs.ts` re-exports these and derives every
 *     LayerPrefs boolean default via `defaultLayerOn` (pinned by
 *     `defaultViewLock.test.ts`);
 *   - server: `LAYER_PREFERENCE_DEFAULTS` below is DERIVED from membership
 *     here (pinned by `layer-preferences.test.ts`), so the API's resolved
 *     defaults can never drift from the lock (#236 — a stray hand-written
 *     `'raw-hotspots': true` is exactly the drift this kills).
 *
 * EXACTLY these four data layers render on a fresh session (no saved prefs).
 * No layer joins the default view without explicit founder authorization =
 * editing this constant AND its pinned tests. CI fails on any drift. (Same
 * pattern as the migration-verb FK + union-literal tests.)
 */
export const DEFAULT_VIEW_LAYERS = [
  'live-video-feeds', // Live Video Feeds
  'aircraft', // Aircraft & Marine
  'reported-oc-wildfires', // Reported OC Wildfires
  'urgent-anomalies', // Urgent Alerts
] as const satisfies readonly LayerKey[];

/**
 * Non-view chrome that defaults ON but is NOT a default-view data layer, and is
 * deliberately exempt from the lock. Each is a modifier/furniture, NOT a hazard
 * layer that "appears" on a fresh COP:
 *   - extreme-weather-warnings: a MUTE on the Urgent Alerts CAP-polygon subset
 *     (ON = un-muted; turning it OFF would HIDE part of an authorized layer).
 *   - ice-storm-road-closures-restrictions: a sub-filter, inert unless Road
 *     Closures is on (it isn't by default) — zero effect on a fresh view.
 *   - sectors: incident-scoped overlay — renders nothing until an incident is
 *     sectored, so it never clutters the national default view.
 *   - provincial-boundaries: base-map furniture (the province outline grid).
 *   - viewshed-on-select: the #262 "hide viewshed" opt-out — a selection
 *     behavior, not a layer (nothing renders until a camera is selected).
 *     Defaults ON per the founder ruling (the drape is the norm).
 * Changing this set ALSO requires editing the pinned tests = founder sign-off.
 */
export const NON_VIEW_DEFAULT_ON_LAYERS = [
  'extreme-weather-warnings',
  'ice-storm-road-closures-restrictions',
  'sectors',
  'provincial-boundaries',
  'viewshed-on-select',
] as const satisfies readonly LayerKey[];

const DEFAULT_ON_SET: ReadonlySet<LayerKey> = new Set<LayerKey>([
  ...DEFAULT_VIEW_LAYERS,
  ...NON_VIEW_DEFAULT_ON_LAYERS,
]);

/**
 * A layer's startup-visible default, derived SOLELY from the locked sets above.
 * ONLY the four DEFAULT_VIEW_LAYERS data layers (plus the documented non-view
 * chrome) start ON; every other layer is OFF. This is the ONLY place a layer's
 * default-on state is computed — no raw `true` lives anywhere in the contract.
 */
export function defaultLayerOn(key: LayerKey): boolean {
  return DEFAULT_ON_SET.has(key);
}

/**
 * The persisted-layer default map, DERIVED from the lock (#236) — there is no
 * hand-written per-layer boolean left to drift. The server merges an operator's
 * stored rows over this via `resolveLayerPreferences`.
 */
export const LAYER_PREFERENCE_DEFAULTS: Readonly<Record<LayerKey, boolean>> = Object.freeze(
  Object.fromEntries(LAYER_KEYS.map((key) => [key, defaultLayerOn(key)])) as Record<
    LayerKey,
    boolean
  >,
);

/** True if `k` is a known persisted layer key. */
export function isLayerKey(k: string): k is LayerKey {
  return Object.prototype.hasOwnProperty.call(LAYER_PREFERENCE_DEFAULTS, k);
}

/* ───────────────────────── WALL layer lock (PART XI) ───────────────────────── *
 *  Design-council PART XI Ruling 1 (2026-06-12, founder-directed — AMENDS the
 *  PART V default-view inheritance): the WALL no longer inherits
 *  DEFAULT_VIEW_LAYERS. The wall is ACTIVE-SITUATION-FORWARD — it shows what is
 *  HAPPENING, not what we have — so its layer membership is its own
 *  founder-locked constant, pinned by `apps/web/src/wallViewLock.test.ts`
 *  (the defaultViewLock pattern verbatim: editing constant + test = founder
 *  sign-off; CI fails on drift).
 * ───────────────────────────────────────────────────────────────────────────── */

/**
 * WALL-VIEW LAYER LOCK (#272, PART XI Ruling 1.1–1.2) — the wall hero's data
 * layers. EXACTLY these four render on the wall map:
 *   - Live Video Feeds is REMOVED (cameras' wall home is the media flanks —
 *     pictures, not dots; with the green field gone, wall map motion collapses
 *     to red-only, Ruling 1.8).
 *   - Extreme Weather Warnings JOINS as a first-class wall layer (on the COP it
 *     is non-view chrome — a mute on the CAP subset; on the wall it is the
 *     weather-situation layer, present at country zoom, Ruling 1.3).
 *   - Aircraft stay: response-in-motion is situation signal.
 * Per-operator prefs/mutes NEVER apply to the wall (Ruling 1.7) — the wall
 * renders this profile, not any operator's pref state.
 */
export const WALL_VIEW_LAYERS = [
  'urgent-anomalies', // Urgent Alerts (all five fused/CAP anomaly sources)
  'reported-oc-wildfires', // Reported OC Wildfires
  'aircraft', // Aircraft & Marine
  'extreme-weather-warnings', // Extreme Weather Warnings (JOINS — Ruling 1.2)
] as const satisfies readonly LayerKey[];

/**
 * Wall base-map furniture (exempt from the wall lock the way
 * NON_VIEW_DEFAULT_ON_LAYERS is exempt from the COP lock): the province
 * outline grid, and the incident-scoped sector overlay (renders nothing on the
 * wall — it has no incident binding — carried for parity with wall v2).
 */
export const WALL_FURNITURE_LAYERS = [
  'provincial-boundaries',
  'sectors',
] as const satisfies readonly LayerKey[];

const WALL_ON_SET: ReadonlySet<LayerKey> = new Set<LayerKey>([
  ...WALL_VIEW_LAYERS,
  ...WALL_FURNITURE_LAYERS,
]);

/**
 * A layer's wall-default visibility, derived SOLELY from the locked wall sets
 * above (the wall sibling of `defaultLayerOn`). `WallMap.tsx` derives every
 * hero layer toggle from this — no raw `true` lives on the wall surface.
 */
export function wallLayerOn(key: LayerKey): boolean {
  return WALL_ON_SET.has(key);
}

/* ───────────── WALL incident-artifact lock (#428, PART XI Ruling 1) ───────────── *
 *  A SEPARATE, CONTEXT-TRIGGERED set — deliberately NOT part of WALL_VIEW_LAYERS
 *  (which stays the four ambient wall layers) and NOT in WALL_ON_SET (so
 *  `wallLayerOn(k)` stays FALSE for each — they are suppressed on the base wall).
 *  These layers are un-suppressed ONLY while an incident is ESCALATED to the wall
 *  (`WallView`/`WallMap`, scoped to that promoted incident, auto-clearing on the
 *  escalation TTL / reset). The wall NEVER mirrors an operator's pref layers
 *  (Ruling 1.7) — the trigger is the escalation context, not any pref state.
 *  Pinned by `apps/web/src/wallViewLock.test.ts` + the shared-types sibling:
 *  editing this set is a founder decision, same rigor as WALL_VIEW_LAYERS.
 * ──────────────────────────────────────────────────────────────────────────────── */

/**
 * WALL incident-artifact layers (#428) — the escalated incident's OWN overlays,
 * un-suppressed context-triggered when a `kind:'incident'` target is on the wall:
 *   - operator-evacuation-orders + bc-evacuation-orders: the evacuation
 *     ORDER/ALERT area polygons (LIVE layers → real order-vs-alert /
 *     active-vs-expired status; #478 "Declared by <authority>"). Vector, low
 *     render risk.
 *   - flood-inundation: the #177 3D modelled what-if. It IS in the lock set
 *     (context-triggered), but its heavy Cesium MOUNT (terrain swap + OSM
 *     buildings on the unattended wall) stays GATED behind a founder Cesium
 *     eyeball AND a persistent on-wall "MODELLED — not an official inundation
 *     product" caveat (the wall is read-only, no click-to-caveat). `WallMap`
 *     keeps the mount OFF until then — evac + framing ship first, the flood
 *     model fast-follows (see apps/web/src/wallIncidentArtifacts.ts).
 * Absent from WALL_ON_SET, so `wallLayerOn` is false for each — membership here is
 * the CONTEXT-trigger authorization, never a default-on.
 */
export const WALL_INCIDENT_ARTIFACT_LAYERS = [
  'operator-evacuation-orders',
  'bc-evacuation-orders',
  'flood-inundation',
] as const satisfies readonly LayerKey[];

const WALL_INCIDENT_ARTIFACT_SET: ReadonlySet<LayerKey> = new Set<LayerKey>(
  WALL_INCIDENT_ARTIFACT_LAYERS,
);

/**
 * True if `key` is a context-triggered wall incident-artifact layer (#428) — a
 * layer the wall un-suppresses ONLY for an escalated incident, never from prefs.
 * `WallMap` derives each artifact toggle from this (not a raw `true`), so a key
 * removed from the lock structurally turns its wall artifact off.
 */
export function isWallIncidentArtifact(key: LayerKey): boolean {
  return WALL_INCIDENT_ARTIFACT_SET.has(key);
}

/** A fully-resolved preference map (every known key present). */
export type LayerPreferenceMap = Record<LayerKey, boolean>;

/** GET response: the resolved preference map (server prefs merged over defaults). */
export const LayerPreferencesResponseSchema = z.object({
  preferences: z.record(z.boolean()),
});
export type LayerPreferencesResponse = z.infer<typeof LayerPreferencesResponseSchema>;

/** PUT body: a partial `{ layer_key: enabled }` map. Unknown keys are ignored by
 *  the server (forward-compatible); values must be booleans. */
export const LayerPreferencesUpdateSchema = z.object({
  preferences: z.record(z.boolean()),
});
export type LayerPreferencesUpdate = z.infer<typeof LayerPreferencesUpdateSchema>;

/** Resolve a stored partial map against the defaults → a complete map. */
export function resolveLayerPreferences(stored: Record<string, boolean>): LayerPreferenceMap {
  const out = { ...LAYER_PREFERENCE_DEFAULTS } as LayerPreferenceMap;
  for (const key of LAYER_KEYS) {
    const v = stored[key];
    if (typeof v === 'boolean') out[key] = v;
  }
  return out;
}
